import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectsAPI } from '../api';
import SEO from '../components/SEO';

function BrowseProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await projectsAPI.listPublic();
      if (response.data.success) {
        setProjects(response.data.projects);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getIconUrl = (iconPath) => {
    if (!iconPath) return null;
    if (iconPath.startsWith('/api/')) {
      const API_URL = process.env.REACT_APP_API_URL || '';
      return API_URL + iconPath.substring(4);
    }
    return iconPath;
  };

  const filteredProjects = projects.filter(project =>
    project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (project.short_description && project.short_description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="browse-page">
      <div className="container">
        <SEO
          title="Browse Projects - SharePanda"
          description="Discover public projects shared by the community on SharePanda. Browse apps, tools, and files from developers."
          url="https://sharepanda.org/browse"
        />
        {/* Header */}
        <div className="browse-header">
          <div>
            <h1>Browse Projects</h1>
            <p className="text-muted">
              Discover amazing projects shared by the community
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading projects...</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon-text">
              {searchQuery ? 'No Results' : 'No Projects'}
            </div>
            <h3>{searchQuery ? 'No projects found' : 'No projects yet'}</h3>
            <p className="text-muted">
              {searchQuery 
                ? 'Try adjusting your search terms' 
                : 'Be the first to share a project!'}
            </p>
          </div>
        ) : (
          <>
            <div className="results-count">
              <span className="text-muted">
                {filteredProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'} found
              </span>
            </div>

            <div className="browse-grid">
              {filteredProjects.map((project) => (
                <Link
                  key={project.project_id}
                  to={`/p/${project.slug}`}
                  className="browse-card"
                >
                  <div className="browse-card-header">
                    {project.thumbnail ? (
                      <img 
                        src={getIconUrl(project.thumbnail)} 
                        alt={project.title}
                        className="browse-card-icon browse-card-icon-img"
                      />
                    ) : (
                      <div className="browse-card-icon">
                        {project.title.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="browse-card-badge">
                      {project.downloads.length > 0 && (
                        <span className="file-count-badge">
                          {project.downloads.length} {project.downloads.length === 1 ? 'file' : 'files'}
                        </span>
                      )}
                    </div>
                  </div>

                  <h3 className="browse-card-title">{project.title}</h3>
                  
                  {project.short_description && (
                    <p className="browse-card-description">
                      {project.short_description}
                    </p>
                  )}

                  <div className="browse-card-stats">
                    <div className="stat-group">
                      <span className="stat-value">
                        {formatNumber(project.stats.total_downloads)}
                      </span>
                      <span className="stat-label">downloads</span>
                    </div>
                    <div className="stat-group">
                      <span className="stat-value">
                        {formatNumber(project.stats.views)}
                      </span>
                      <span className="stat-label">views</span>
                    </div>
                  </div>

                  <div className="browse-card-footer">
                    <span className="text-small text-muted">
                      Updated {formatDate(project.updated_at)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BrowseProjects;
